import { Separator, Text } from '@cuemath/web-comps';
import React from 'react';
import {
  Button,
  Form,
  Icon,
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
  Segment,
  TextArea,
} from 'semantic-ui-react';

import COLORS from '../../../constants/colors';
import { usePostEvalComplianceReverse } from 'actions/eval-prep-visibility';

const ReverseViolation = ({ violation, closeModal, onReversed }) => {
  const [reason, setReason] = React.useState('');
  const [reverseError, setReverseError] = React.useState(null);
  const [isDone, setIsDone] = React.useState(false);

  const { post: postReverse, isProcessing } = usePostEvalComplianceReverse({
    onComplete: error => {
      if (error) {
        setReverseError(error || 'Could not reverse this violation.');

        return;
      }
      setReverseError(null);
      setIsDone(true);
    },
  });

  const handleReverse = React.useCallback(() => {
    setReverseError(null);
    postReverse({ reason: reason.trim() }, violation.id);
  }, [postReverse, reason, violation.id]);

  const handleDone = React.useCallback(() => {
    if (isDone && onReversed) onReversed();
    closeModal();
  }, [isDone, onReversed, closeModal]);

  return (
    <Modal open size="small" onClose={handleDone}>
      <ModalHeader>
        <Text variant="h6">Reverse violation</Text>
        <Text variant="body3" color={COLORS.GRAY}>
          {violation.teacher_name || violation.teacher_id}
        </Text>
      </ModalHeader>
      <ModalContent>
        <ModalDescription>
          {isDone ? (
            <Segment>
              <Text variant="body1Medium" color={COLORS.DARK_GREEN}>
                <Icon name="check circle" /> Reversed. The{' '}
                {violation.points_deducted} points are back on this coach&apos;s
                rating.
              </Text>
            </Segment>
          ) : (
            <>
              <Text variant="body2">
                {violation.display_name || violation.violation_type} &middot;{' '}
                {violation.points_deducted} points &middot;{' '}
                {violation.violation_category}
              </Text>
              <Separator height={14} transparent />
              <Form>
                <Text variant="body1Medium">Why is this being reversed?</Text>
                <Separator height={4} transparent />
                <TextArea
                  rows={3}
                  value={reason}
                  placeholder="Recorded against the violation"
                  onChange={(_, { value }) => {
                    setReason(value);
                    setReverseError(null);
                  }}
                  aria-label="Reversal reason"
                />
              </Form>
              <Separator height={10} transparent />
              <Text variant="body3" color={COLORS.GRAY}>
                The rating is derived from active violations, so the points return
                as soon as this is reversed.
              </Text>
            </>
          )}
          {reverseError && (
            <>
              <Separator height={12} transparent />
              <Text variant="body2" color={COLORS.RUSTY_RED}>
                {reverseError}
              </Text>
            </>
          )}
        </ModalDescription>
      </ModalContent>
      <ModalActions>
        {isDone ? (
          <Button color="black" onClick={handleDone}>
            Done
          </Button>
        ) : (
          <>
            <Button basic onClick={closeModal}>
              Cancel
            </Button>
            <Button
              color="black"
              loading={isProcessing}
              disabled={!reason.trim() || isProcessing}
              onClick={handleReverse}
            >
              Reverse
            </Button>
          </>
        )}
      </ModalActions>
    </Modal>
  );
};

export default ReverseViolation;
